import { useEffect, useState } from 'react';
import {
  Activity,
  CalendarDays,
  CheckCircle2,
  Flame,
  Loader2,
  Pencil,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * The money row at the top of the Budgets page: Budget, Spent, Remaining,
 * Current daily budget, Suggested daily, Restart on the 1st/15th, Days in
 * cycle. One row, one number per thing. Planner figures come from the pacing
 * snapshot; the two daily figures come from the autonomous plan the pacing
 * card computed, handed up through `AutonomousPacingSummary`.
 */

export interface AutonomousPacingSummary {
  /** Live daily budget today: Ads, plus the LSA account when combined and known. */
  currentMicros: number;
  currentAdsMicros: number;
  lsaCurrentMicros: number | null;
  /** What Core sets on the next tick: Ads plan, plus the LSA plan when combined and known. */
  suggestedMicros: number;
  suggestedAdsMicros: number;
  /** The Ads reservation the plan aims at; differs from the Ads plan only when a clamp holds a campaign back. */
  targetAdsMicros: number;
  lsaPlanMicros: number | null;
  lsaPlanIsReservation: boolean;
  combine: boolean;
  sharedSkip: boolean;
  hasLsa: boolean;
  /** The split table has edits that are not saved; the headline ignores them. */
  unsavedEdits: boolean;
  /** The tick already ran this window; the plan is pinned to the live daily until the next one. */
  heldThisWindow: boolean;
  /** Core refused to plan (skip reason) and the pacing card explains the fix. */
  refusalReason: string | null;
  refusalTitle: string | null;
  /** Planner's LSA reservation (LSA remaining ÷ days left); the Target tile uses it so Target matches Planner. */
  lsaReservationMicros: number | null;
}

interface MoneyStripCycle {
  start: string;
  end: string;
  days_left: number;
  days_left_inclusive?: boolean | null;
  totals: {
    budget_usd: number;
    spent_usd: number;
    remaining_usd: number;
    suggested_daily_usd: number;
    ideal_daily_usd: number;
    incoming_cumulative_usd?: number | null;
  };
}

interface BudgetMoneyStripProps {
  cycle: MoneyStripCycle | null;
  loading?: boolean;
  billingCycleStartDay?: number | null;
  spendSource?: string | null;
  plannerSourced: boolean;
  /** Planner's per-client convention for the days-left divisor; undefined when unknown. */
  daysLeftInclusive?: boolean | null;
  autonomous: AutonomousPacingSummary | null;
  canEditBudget: boolean;
  savingBudget: boolean;
  onSaveBudget?: (budgetUsd: number) => void;
  /** Ads account settings just saved and the plan is being recomputed; the three daily tiles wait. */
  recalculating?: boolean;
}

function formatUsd(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value ?? 0);
}

function usdFromMicros(micros: number | null | undefined): string {
  return formatUsd((micros ?? 0) / 1_000_000);
}

function daysInCycle(start: string | undefined, end: string | undefined): number | null {
  if (!start || !end) return null;
  const a = Date.parse(`${start}T00:00:00Z`);
  const b = Date.parse(`${end}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  return Math.round((b - a) / 86_400_000) + 1;
}

const CENT_MICROS = 10_000;

export function BudgetMoneyStrip({
  cycle,
  loading = false,
  billingCycleStartDay,
  spendSource,
  plannerSourced,
  daysLeftInclusive,
  autonomous,
  canEditBudget,
  savingBudget,
  onSaveBudget,
  recalculating = false,
}: BudgetMoneyStripProps) {
  const dash = '—';
  const total = daysInCycle(cycle?.start, cycle?.end);

  const refused = Boolean(autonomous?.refusalReason);
  const refusalNote = autonomous?.refusalTitle
    ? `Core did not plan this account: ${autonomous.refusalTitle}. See the pacing card for the fix.`
    : 'Core did not plan this account. See the pacing card for the fix.';

  // Target daily: the Planner reservation. Ads target + Planner's LSA
  // reservation when combined, so this equals Planner's own Suggested daily.
  let targetValue = dash;
  let targetSub: string | undefined;
  if (autonomous && !refused) {
    const lsaPart = autonomous.combine ? autonomous.lsaReservationMicros : null;
    const partial = autonomous.combine && lsaPart == null;
    targetValue = usdFromMicros(autonomous.targetAdsMicros + (lsaPart ?? 0)) + (partial ? ', partial' : '');
    const parts: string[] = [
      cycle ? `Remaining ÷ ${cycle.days_left} day${cycle.days_left === 1 ? '' : 's'}, from Planner` : 'Remaining ÷ days left, from Planner',
    ];
    if (autonomous.combine) {
      parts.push(`Ads ${usdFromMicros(autonomous.targetAdsMicros)} + LSA ${lsaPart == null ? 'unknown' : usdFromMicros(lsaPart)}`);
    } else if (autonomous.hasLsa) {
      parts.push('Ads only');
    }
    parts.push('Suggested daily walks toward this 15% a tick; raises are capped at 1.25× the last 7 days\' spend');
    targetSub = parts.join('. ');
  } else if (refused) {
    targetSub = refusalNote;
  } else if (cycle) {
    targetValue = formatUsd(cycle.totals.suggested_daily_usd);
    targetSub = 'Remaining ÷ days left, from Planner (Ads + LSA together)';
  }

  // Current daily: the plan's live read; Planner has no daily budget figure.
  let currentValue = dash;
  let currentSub: string | undefined;
  if (autonomous) {
    const partial = autonomous.combine && autonomous.lsaCurrentMicros == null;
    currentValue = usdFromMicros(autonomous.currentMicros) + (partial ? ', partial' : '');
    currentSub = autonomous.sharedSkip
      ? 'Shared pot, counted once'
      : autonomous.combine
        ? `Ads ${usdFromMicros(autonomous.currentAdsMicros)} + LSA ${
            autonomous.lsaCurrentMicros == null ? 'unknown' : usdFromMicros(autonomous.lsaCurrentMicros)
          }`
        : autonomous.hasLsa
          ? 'Ads only'
          : undefined;
  } else if (!loading && cycle) {
    currentSub = 'Waiting for the pacing plan';
  }

  // Suggested daily: what Core sets on Monday/Thursday. Planner's remaining ÷
  // days left is the fallback when no plan exists (for example Planner-only clients).
  let suggestedValue = dash;
  let suggestedSub: string | undefined;
  if (autonomous && refused) {
    suggestedSub = refusalNote;
  } else if (autonomous) {
    if (autonomous.sharedSkip) {
      suggestedValue = usdFromMicros(autonomous.currentAdsMicros);
      suggestedSub = 'Not written, one shared wallet cannot be split';
    } else {
      const partial = autonomous.combine && autonomous.lsaPlanMicros == null;
      suggestedValue = usdFromMicros(autonomous.suggestedMicros) + (partial ? ', partial' : '');
      // Meaning first, composition second, caveats last.
      const parts: string[] = ['What Core sets on Monday and Thursday'];
      if (autonomous.combine) {
        parts.push(
          `Ads ${usdFromMicros(autonomous.suggestedAdsMicros)} + LSA ${
            autonomous.lsaPlanMicros == null ? 'unknown' : usdFromMicros(autonomous.lsaPlanMicros)
          }${autonomous.lsaPlanIsReservation && autonomous.lsaPlanMicros != null ? ' (Planner reservation)' : ''}`,
        );
      } else if (autonomous.hasLsa) {
        parts.push('Ads only, LSA planned on its own line');
      }
      if (autonomous.heldThisWindow) {
        parts.push('held at the live daily until the next tick');
      } else if (Math.abs(autonomous.suggestedAdsMicros - autonomous.targetAdsMicros) >= CENT_MICROS) {
        parts.push('below Target daily, the guards move it 15% a tick');
      }
      if (autonomous.unsavedEdits) parts.push('table has unsaved edits, not counted here');
      suggestedSub = parts.join(', ');
    }
  } else if (cycle) {
    suggestedValue = formatUsd(cycle.totals.suggested_daily_usd);
    suggestedSub = `Remaining ÷ ${cycle.days_left} day${cycle.days_left === 1 ? '' : 's'}${
      daysLeftInclusive == null ? '' : daysLeftInclusive ? ', today included' : ', today excluded'
    }, from Planner`;
  }

  // After an account settings save the plan is stale until the preview
  // returns; the three plan-derived tiles say so instead of showing old money.
  if (recalculating) {
    currentValue = 'Recalculating';
    suggestedValue = 'Recalculating';
    targetValue = 'Recalculating';
    currentSub = 'Applying the new account settings';
    suggestedSub = 'Applying the new account settings';
    targetSub = 'Applying the new account settings';
  }

  return (
    <div
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
      data-testid="budget-money-strip"
    >
      <BudgetPacingBudgetMetric
        valueUsd={cycle?.totals.budget_usd ?? 0}
        display={cycle ? undefined : dash}
        sub={
          cycle?.totals.incoming_cumulative_usd
            ? `${formatUsd(cycle.totals.incoming_cumulative_usd)} carryover`
            : plannerSourced
              ? 'Planner contract'
              : undefined
        }
        canEdit={canEditBudget && Boolean(cycle)}
        saving={savingBudget}
        onSave={onSaveBudget}
      />
      <BudgetPacingMetric
        label="Spent"
        value={cycle ? formatUsd(cycle.totals.spent_usd) : dash}
        icon={<Flame className="h-4 w-4" />}
        sub={
          spendSource === 'planner_recorded'
            ? 'Google Ads + LSA, recorded in the Agile Planner'
            : spendSource === 'live_google_ads'
              ? 'Live Google Ads, not Planner-sourced'
              : undefined
        }
      />
      <BudgetPacingMetric
        label="Remaining"
        value={cycle ? formatUsd(cycle.totals.remaining_usd) : dash}
        icon={<CheckCircle2 className="h-4 w-4" />}
        tone={cycle && cycle.totals.remaining_usd < 0 ? 'danger' : 'good'}
        sub={plannerSourced ? 'Planner budget + carryover − spent' : undefined}
      />
      <BudgetPacingMetric
        label="Current daily budget"
        value={currentValue}
        icon={<Activity className="h-4 w-4" />}
        sub={currentSub}
      />
      <BudgetPacingMetric
        label="Suggested daily"
        value={suggestedValue}
        icon={<TrendingUp className="h-4 w-4" />}
        tone="accent"
        sub={suggestedSub}
      />
      <BudgetPacingMetric
        label="Target daily"
        value={targetValue}
        icon={<TrendingUp className="h-4 w-4" />}
        sub={targetSub}
      />
      <BudgetPacingMetric
        label={billingCycleStartDay === 15 ? 'Restart on the 15th' : 'Restart on the 1st'}
        value={cycle ? formatUsd(cycle.totals.ideal_daily_usd) : dash}
        icon={<TrendingUp className="h-4 w-4" />}
        sub="Budget ÷ cycle days, the even daily pace"
      />
      <BudgetPacingMetric
        label="Days in cycle"
        value={
          cycle
            ? cycle.days_left > 0
              ? `${cycle.days_left} left`
              : 'Cycle ended'
            : dash
        }
        icon={<CalendarDays className="h-4 w-4" />}
        sub={
          cycle
            ? `${total != null ? `of ${total} · ` : ''}${cycle.start} → ${cycle.end}`
            : undefined
        }
      />
    </div>
  );
}

export function BudgetPacingBudgetMetric({
  valueUsd,
  display,
  sub,
  canEdit,
  saving,
  onSave,
}: {
  valueUsd: number;
  /** Override for the read-only figure (for example a dash while loading). */
  display?: string;
  sub?: string;
  canEdit: boolean;
  saving: boolean;
  onSave?: (budgetUsd: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(Math.round(valueUsd * 100) / 100));

  useEffect(() => {
    if (!editing) {
      setDraft(String(Math.round(valueUsd * 100) / 100));
    }
  }, [valueUsd, editing]);

  const save = () => {
    const parsed = Number.parseFloat(draft.replace(/[$,]/g, '').trim());
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error('Enter a budget greater than 0.');
      return;
    }
    onSave?.(parsed);
    setEditing(false);
  };

  return (
    <div className="rounded-md border border-border bg-warm-bg px-3 py-2">
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-2">
          <Wallet className="h-4 w-4" />
          Budget
        </span>
        {canEdit && !editing ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-coral hover:text-coral-dark"
            onClick={() => setEditing(true)}
          >
            <Pencil className="h-3 w-3" />
            Edit
          </button>
        ) : null}
      </div>
      {editing ? (
        <div className="mt-2 flex items-center gap-2">
          <Input
            aria-label="Pacing budget"
            type="number"
            min={1}
            step={1}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') save();
              if (event.key === 'Escape') setEditing(false);
            }}
            className="h-8 w-full tabular-nums"
          />
          <Button
            type="button"
            size="sm"
            className="bg-coral hover:bg-coral-hover"
            disabled={saving}
            onClick={save}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      ) : (
        <div className="mt-1 text-lg font-semibold tabular-nums text-foreground">
          {display ?? formatUsd(valueUsd)}
        </div>
      )}
      {sub ? <div className="text-xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

export function BudgetPacingMetric({
  label,
  value,
  icon,
  sub,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  icon: JSX.Element;
  sub?: string;
  tone?: 'neutral' | 'good' | 'danger' | 'accent';
}) {
  const toneClass =
    tone === 'danger'
      ? 'text-destructive'
      : tone === 'good'
        ? 'text-[hsl(var(--status-success))]'
        : tone === 'accent'
          ? 'text-coral-dark'
          : 'text-foreground';
  return (
    <div className="rounded-md border border-border bg-warm-bg px-3 py-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className={`mt-1 text-lg font-semibold tabular-nums ${toneClass}`}>
        {value}
      </div>
      {sub ? <div className="text-xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}
